import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { PARTNERS_SITE_URL } from '../lib/links';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      // The API answers identically for a registered and an unknown address, so the
      // screen must not claim the email exists either.
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the reset link. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col justify-center px-8 sm:px-12 py-12">
      <div className="max-w-md w-full mx-auto">
        <Link to="/login" className="flex w-fit items-center gap-2 text-[#64748B] hover:text-[#1A1A1A] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to sign in</span>
        </Link>

        <a href={PARTNERS_SITE_URL} className="block w-fit mb-8">
          <img src="/light_logo.webp" alt="Expedify Logo" className="h-10 w-auto" />
        </a>

        {sent ? (
          <div data-testid="forgot-sent">
            <div className="w-12 h-12 rounded-full bg-[#ee785b]/10 flex items-center justify-center mb-5">
              <MailCheck className="w-6 h-6 text-[#ee785b]" />
            </div>
            <h1 className="text-3xl font-semibold text-[#1A1A1A] mb-2">Check your inbox</h1>
            <p className="text-[#64748B]">
              If <span className="font-medium text-[#1A1A1A]">{email}</span> is registered, a reset
              link is on its way. It works once and expires in an hour.
            </p>
            <p className="mt-6 text-sm text-[#64748B]">
              Nothing arrived? Check spam, or{' '}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-[#ee785b] font-semibold hover:underline"
              >
                try a different address
              </button>
              .
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-semibold text-[#1A1A1A] mb-2">Forgot your password?</h1>
            <p className="text-[#64748B] mb-8">
              Enter the email you signed up with and we will send you a link to choose a new one.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#1A1A1A]">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1.5 h-12 rounded-lg border-[#E2E2DF] focus:ring-2 focus:ring-[#ee785b]/20 focus:border-[#ee785b]"
                  required
                  data-testid="forgot-email"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#ee785b] hover:bg-[#d66547] text-white rounded-lg font-semibold btn-press"
                data-testid="forgot-submit"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
